import React from "react";

/**
 * ReportSummaryCard — Reusable Metric / KPI Card for Reports
 * 
 * Props:
 * - label: Upper heading text
 * - value / val: Metric number or formatted string (e.g. 1600 or "₹11,76,44,058.00")
 * - icon: FontAwesome icon string (e.g. "fa-users", "fa-clock") or React element
 * - bg: Background color class (e.g. "bg-[#E9DFFF]")
 * - border: Border color class (e.g. "border-[#7C3AED]/25")
 * - accent: Text/accent color class (e.g. "text-[#7C3AED]")
 * - iconBg: Icon container background (default: "bg-white/80")
 * - variant: Legacy preset variant for footer badges ('emerald' | 'rose' | 'red' | 'primary' | 'purple')
 * - formatCurrency: boolean (default: true for legacy badges)
 * - className: Custom outer container classes
 */
const ReportSummaryCard = ({
  label,
  value,
  val,
  icon,
  bg,
  border,
  accent,
  iconBg = "bg-white/80",
  variant = "primary",
  formatCurrency = true,
  className = "",
}) => {
  const actualValue = val !== undefined ? val : value;

  // Icon-based card layout (matching Future Demand Report style)
  if (icon) {
    const formattedValue =
      typeof actualValue === "number"
        ? actualValue.toLocaleString("en-IN")
        : (actualValue ?? "0");

    return (
      <div
        className={[
          bg || "bg-white",
          border || "border-border-light",
          "border py-2.5 px-3.5 rounded-xl shadow-xs flex items-center justify-between transition-all hover:shadow-sm",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="flex flex-col min-w-0 pr-2">
          <span
            className={[
              "text-[10px] font-bold uppercase tracking-wider mb-1 truncate",
              accent || "text-ink-500",
            ].join(" ")}
            title={label}
          >
            {label}
          </span>
          <span
            className={[
              "text-base md:text-lg font-black truncate",
              accent || "text-ink-900",
            ].join(" ")}
          >
            {formattedValue}
          </span>
        </div>
        <div
          className={[
            "w-9 h-9 rounded-xl flex items-center justify-center text-sm flex-shrink-0 shadow-xs",
            iconBg,
            accent || "text-ink-700",
          ].join(" ")}
        >
          {typeof icon === "string" ? (
            <i className={`fa-solid ${icon}`}></i>
          ) : (
            icon
          )}
        </div>
      </div>
    );
  }

  // Legacy compact badge style (for ReportFooter)
  const variantStyles = {
    emerald: "text-emerald-600",
    rose: "text-rose-600",
    red: "text-red-600",
    primary: "text-primary",
    purple: "text-purple-600",
  };

  const footerFormattedValue =
    formatCurrency && typeof actualValue === "number"
      ? `₹${actualValue.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : typeof actualValue === "number"
        ? actualValue.toLocaleString("en-IN")
        : (actualValue ?? "0");

  return (
    <div
      className={[
        "bg-white px-3.5 py-1.5 rounded-xl border border-border-light shadow-sm flex flex-col gap-0.5",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="text-[9px] font-bold text-ink-400 uppercase tracking-wider">
        {label}
      </span>
      <span
        className={[
          "text-base font-bold",
          variantStyles[variant] || variantStyles.primary,
        ].join(" ")}
      >
        {footerFormattedValue}
      </span>
    </div>
  );
};

export default ReportSummaryCard;
